/**
 * Cycle de vie des détours (way in/out).
 *
 * Un détour permet de quitter le parcours officiel en un point, de couper par
 * la route pour dépasser la course sans rouler sur son itinéraire, puis de le
 * rejoindre plus loin. Il est routé en **profil voiture forcé**, indépendamment
 * du profil de la trace : c'est un suiveur en voiture qui l'emprunte.
 *
 * Le contrôleur porte la création guidée, l'édition, la suppression, le
 * routage, la synchronisation des marqueurs dérivés et la complétion des
 * géométries au démarrage.
 *
 * Référence : `WayInOutController.kt`, `docs/way-in-out.md`.
 */

import type { GpxTrack, DistanceCandidate } from '../core/track'
import type { Storage } from '../core/storage'
import type { Translator } from '../i18n'
import type { WayInOut, WayVia } from '../core/types'
import { LIMITS, ROUTING_PROFILES } from '../core/types'
import { apiProfileName, requestRoute } from '../core/osrm'
import {
  orderVias,
  sameWayDefinition,
  toTrackPoints,
  wayCacheKey,
} from '../core/wayInOut'
import type { PolylinePoint } from '../core/polyline'
import type { TrackPoint } from '../core/types'
import { confirmDialog, showDialog } from './dialogs'
import type { MapPickController } from './mapPick'
import type { Shell } from './shell'
import { textField } from './controls'
import { formatKmForInput, parseKmInput } from './markerController'

export interface WayInOutDeps {
  readonly shell: Shell
  readonly translator: Translator
  readonly picker: MapPickController
  readonly storage: Storage
  /** Traces vivantes : un résultat de routage arrivé trop tard est jeté. */
  readonly tracks: () => readonly GpxTrack[]
  /** Redessine la trace et enregistre les modifications. */
  readonly onTrackChanged: (track: GpxTrack) => void
  /** Choix parmi plusieurs points kilométriques, en convention marqueur. */
  readonly chooseKm: (
    track: GpxTrack,
    candidates: readonly DistanceCandidate[],
    onChosen: (km: number) => void,
  ) => void
  /** Symbole des marqueurs dérivés, tiré du type prédéfini. */
  readonly wayOutSymbol: () => string
}

export class WayInOutController {
  /** Routages en cours, pour pouvoir les abandonner. */
  #jobs = new Map<string, AbortController>()

  constructor(private readonly deps: WayInOutDeps) {}

  /* -------------------------------------------------------------------
   * Création guidée
   * ---------------------------------------------------------------- */

  /**
   * Démarre la création à partir du point de sortie déjà choisi.
   *
   * Appelée depuis le dialogue d'ajout de marqueur, dont le type « way out »
   * ne crée pas un marqueur mais un détour : le point kilométrique saisi
   * devient le point de sortie.
   */
  startCreation(track: GpxTrack, exitKm: number, name: string): void {
    const t = this.deps.translator

    this.deps.picker.startTrackPick(
      track,
      t.t('way_pick_entry_hint'),
      (candidates) => {
        // Tap hors trace : abandon, même convention que le déplacement d'un
        // marqueur.
        if (candidates.length === 0) return

        this.deps.chooseKm(track, candidates, (entryKm) => {
          this.#askVia(track, exitKm, entryKm, name, [])
        })
      },
    )
  }

  /**
   * Boucle des points de passage.
   *
   * Reproposée après chaque point jusqu'au calcul ou au plafond. L'ordre des
   * taps est sans importance : la liste complète est réordonnée à la fin, donc
   * un point oublié peut être posé en revenant en arrière sur le trajet.
   */
  #askVia(
    track: GpxTrack,
    exitKm: number,
    entryKm: number,
    name: string,
    vias: WayVia[],
  ): void {
    const t = this.deps.translator

    if (vias.length >= LIMITS.MAX_VIAS_PER_WAY) {
      void this.#create(track, exitKm, entryKm, name, vias)
      return
    }

    showDialog(document.body, {
      title: t.t('way_via_title'),
      body:
        vias.length === 0
          ? t.t('way_via_message')
          : t.t(
              vias.length === 1
                ? 'way_via_more_message_one'
                : 'way_via_more_message',
              { count: vias.length },
            ),
      dismissible: false,
      actions: [
        {
          label: vias.length === 0 ? t.t('way_via_skip') : t.t('way_via_done'),
          onClick: () => void this.#create(track, exitKm, entryKm, name, vias),
        },
        {
          label: t.t('way_via_pick'),
          primary: true,
          onClick: () => {
            this.deps.picker.startMapPick(
              t.t('way_pick_via_hint'),
              (position) => {
                vias.push({ lat: position.lat, lon: position.lon })
                this.#askVia(track, exitKm, entryKm, name, vias)
              },
            )
          },
        },
      ],
    })
  }

  async #create(
    track: GpxTrack,
    exitKm: number,
    entryKm: number,
    name: string,
    vias: readonly WayVia[],
  ): Promise<void> {
    const way: WayInOut = {
      id: crypto.randomUUID(),
      name,
      exitKm,
      entryKm,
      vias: this.#orderViasFor(track, exitKm, entryKm, vias),
      visible: true,
    }

    track.wayInOuts.push(way)
    this.#syncMarkers(track)
    await this.#computeRoute(track, way, false)
  }

  /* -------------------------------------------------------------------
   * Modifications
   * ---------------------------------------------------------------- */

  /**
   * Remplace un détour.
   *
   * @param reroute Recalculer l'itinéraire, quand la géométrie d'entrée a
   *   changé. Un simple changement de nom ou de visibilité ne le demande pas.
   */
  async #applyChange(
    track: GpxTrack,
    updated: WayInOut,
    reroute: boolean,
  ): Promise<void> {
    const index = track.wayInOuts.findIndex((way) => way.id === updated.id)
    if (index === -1) return

    track.wayInOuts[index] = updated
    this.#syncMarkers(track)

    if (!reroute) return

    // L'ancienne géométrie est retirée avant l'appel : la laisser afficherait
    // un détour qui ne correspond plus aux points saisis.
    track.wayRoutes.delete(updated.id)
    this.deps.onTrackChanged(track)

    await this.#computeRoute(track, updated, false)
  }

  /** Déplacement du point de sortie, depuis la bulle du marqueur dérivé. */
  moveExit(track: GpxTrack, wayId: string, exitKm: number): void {
    const way = track.wayInOuts.find((candidate) => candidate.id === wayId)
    if (!way) return

    void this.#applyChange(track, { ...way, exitKm }, true)
  }

  /**
   * Nouvelle position d'un point de passage, après glissement sur la carte.
   *
   * Le modèle porte déjà la position visée — c'est ce qui a permis l'aperçu
   * pendant le geste ; il ne reste qu'à l'enregistrer et à recalculer.
   */
  moveVia(
    track: GpxTrack,
    wayId: string,
    viaIndex: number,
    position: { lat: number; lon: number },
  ): void {
    const way = track.wayInOuts.find((candidate) => candidate.id === wayId)
    if (!way || viaIndex < 0 || viaIndex >= way.vias.length) return

    const vias = way.vias.map((via, index) =>
      index === viaIndex ? { lat: position.lat, lon: position.lon } : via,
    )

    void this.#applyChange(track, { ...way, vias }, true)
  }

  /** Visibilité : marqueur dérivé, ligne pointillée et points de passage. */
  setVisible(track: GpxTrack, wayId: string, visible: boolean): void {
    const way = track.wayInOuts.find((candidate) => candidate.id === wayId)
    if (!way) return

    void this.#applyChange(track, { ...way, visible }, false)
  }

  delete(track: GpxTrack, wayId: string): void {
    this.#jobs.get(wayId)?.abort()
    this.#jobs.delete(wayId)

    track.wayInOuts = track.wayInOuts.filter((way) => way.id !== wayId)
    track.wayRoutes.delete(wayId)
    void this.deps.storage.deleteCache(wayCacheKey(wayId))

    this.#syncMarkers(track)
  }

  /** Trace supprimée : abandonne les routages en cours et purge les caches. */
  onTrackDeleted(track: GpxTrack): void {
    for (const way of track.wayInOuts) {
      this.#jobs.get(way.id)?.abort()
      this.#jobs.delete(way.id)
      void this.deps.storage.deleteCache(wayCacheKey(way.id))
    }
  }

  /* -------------------------------------------------------------------
   * Routage
   * ---------------------------------------------------------------- */

  /**
   * Calcule l'itinéraire d'un détour et le met en cache.
   *
   * Les points de sortie et de rentrée sont résolus sur la trace au moment de
   * l'appel : ils dépendent du départ réel, qui a pu changer entre-temps.
   *
   * @param quiet Ne rien signaler en cas d'échec. Vrai au démarrage et à
   *   l'import, où l'utilisateur n'a rien demandé et peut être hors ligne.
   */
  async #computeRoute(
    track: GpxTrack,
    way: WayInOut,
    quiet: boolean,
  ): Promise<void> {
    this.#jobs.get(way.id)?.abort()

    const exit = track.pointAtDistance(track.markerKmToRawDistance(way.exitKm))
    const entry = track.pointAtDistance(track.markerKmToRawDistance(way.entryKm))
    if (!exit || !entry) return

    const waypoints = [
      { lat: exit.lat, lon: exit.lon },
      ...way.vias.map((via) => ({ lat: via.lat, lon: via.lon })),
      { lat: entry.lat, lon: entry.lon },
    ]

    const controller = new AbortController()
    this.#jobs.set(way.id, controller)

    // Un détour à plusieurs points de passage demande plusieurs secondes de
    // calcul à OSRM. Sans bandeau, rien ne distingue une attente d'un échec
    // silencieux. Il n'est pas annulable : l'appel part de toute façon, et
    // seul un autre calcul ou une suppression l'interrompt réellement.
    const dismissProgress = quiet
      ? (): void => {}
      : this.deps.shell.showSnackbar(
          this.deps.translator.t('way_routing_in_progress'),
          undefined,
          // Durée large : le bandeau est retiré à la fin du calcul, cette
          // valeur n'est qu'un garde-fou si la promesse n'aboutit jamais.
          60_000,
        )

    const profile = apiProfileName(ROUTING_PROFILES.CAR)
    let result: { points: readonly PolylinePoint[] } | null = null

    try {
      // « Passe par ces points » : au-delà de deux points de passage, on laisse
      // OSRM les ordonner sur le réseau routier réel, ce qu'un tri à vol
      // d'oiseau ne sait pas faire. Repli sur l'ordre calculé côté client.
      if (way.vias.length >= 2) {
        result = await requestRoute({
          waypoints,
          profile,
          service: 'trip',
          signal: controller.signal,
        })
      }

      result ??= await requestRoute({
        waypoints,
        profile,
        signal: controller.signal,
      })
    } catch {
      // Annulation : un autre calcul a pris la main, ou le détour a disparu.
      return
    } finally {
      dismissProgress()
      if (this.#jobs.get(way.id) === controller) this.#jobs.delete(way.id)
    }

    // La trace ou le détour a pu être supprimé pendant l'appel.
    if (!this.deps.tracks().includes(track)) return
    if (!track.wayInOuts.some((candidate) => candidate.id === way.id)) return

    if (result === null) {
      if (!quiet) {
        this.deps.shell.showSnackbar(
          this.deps.translator.t('way_routing_error'),
        )
      }
      return
    }

    const points = toTrackPoints(result.points)
    track.wayRoutes.set(way.id, points)
    await this.deps.storage.putCache(wayCacheKey(way.id), points)
    this.deps.onTrackChanged(track)
  }

  /**
   * Complète les géométries manquantes d'une trace.
   *
   * Cache local d'abord, calcul en dernier recours et en silence : appelée au
   * démarrage et à l'import, où l'utilisateur n'a rien demandé. Une géométrie
   * déjà en mémoire — embarquée dans le GPX importé — est écrite au cache sans
   * aucun appel réseau, ce qui évite de solliciter OSRM sur une ligne de départ
   * où le réseau est saturé.
   */
  async ensureRoutes(track: GpxTrack): Promise<void> {
    for (const way of track.wayInOuts) {
      if (this.#jobs.has(way.id)) continue

      const inMemory = track.wayRoutes.get(way.id)
      if (inMemory && inMemory.length >= 2) {
        await this.deps.storage.putCache(wayCacheKey(way.id), inMemory)
        continue
      }

      const cached = await this.deps.storage.getCache<TrackPoint[]>(
        wayCacheKey(way.id),
      )

      if (!this.deps.tracks().includes(track)) return

      if (cached && cached.length >= 2) {
        track.wayRoutes.set(way.id, cached)
        this.deps.onTrackChanged(track)
      } else {
        await this.#computeRoute(track, way, true)
      }
    }
  }

  /**
   * Récupère la géométrie lue dans un fichier pour les détours restaurés.
   *
   * Les identifiants ne survivent pas au GPX — ils sont régénérés à l'import —
   * si bien qu'après une purge du cache, la seule façon de retrouver le tracé
   * embarqué est de comparer les définitions.
   */
  adoptFileRoutes(
    track: GpxTrack,
    fileWays: readonly WayInOut[],
    fileRoutes: ReadonlyMap<string, readonly PolylinePoint[]>,
  ): void {
    for (const way of track.wayInOuts) {
      if (track.wayRoutes.has(way.id)) continue

      const match = fileWays.find((candidate) =>
        sameWayDefinition(candidate, way),
      )
      if (!match) continue

      const route = fileRoutes.get(match.id)
      if (route && route.length >= 2) {
        track.wayRoutes.set(way.id, toTrackPoints(route))
      }
    }
  }

  /* -------------------------------------------------------------------
   * Édition
   * ---------------------------------------------------------------- */

  /**
   * Dialogue d'édition d'un détour.
   *
   * @param onPickFlow Appelé avant de rendre la main à la carte, pour que le
   *   dialogue parent — la liste des marqueurs — libère l'écran.
   */
  showEditDialog(
    track: GpxTrack,
    wayId: string,
    options: { onPickFlow?: () => void; onDone?: () => void } = {},
  ): void {
    const way = track.wayInOuts.find((candidate) => candidate.id === wayId)
    if (!way) return

    const t = this.deps.translator
    const onDone = options.onDone ?? ((): void => {})
    const body = document.createElement('div')

    const name = textField({ label: t.t('marker_name'), value: way.name })
    const exit = textField({
      label: t.t('way_exit_km'),
      value: formatKmForInput(way.exitKm),
      inputMode: 'decimal',
    })
    const entry = textField({
      label: t.t('way_entry_km'),
      value: formatKmForInput(way.entryKm),
      inputMode: 'decimal',
    })

    body.append(name.element, exit.element, entry.element)

    const state = document.createElement('div')
    state.className = 'setting-row__summary'
    state.textContent =
      way.vias.length === 0
        ? t.t('way_via_state_none')
        : t.t('way_via_state_count', { count: way.vias.length })
    body.append(state)

    /** Champs saisis, repliés sur la valeur en place si la saisie est vide. */
    const read = (): { name: string; exitKm: number; entryKm: number } => ({
      name: name.input.value.trim() || way.name,
      exitKm: parseKmInput(exit.input.value) ?? way.exitKm,
      entryKm: parseKmInput(entry.input.value) ?? way.entryKm,
    })

    const secondary = document.createElement('div')
    secondary.className = 'way-actions'

    const button = (label: string, onClick: () => void): HTMLButtonElement => {
      const element = document.createElement('button')
      element.type = 'button'
      element.className = 'button button--outlined'
      element.textContent = label
      element.addEventListener('click', onClick)
      return element
    }

    let close = (): void => {}

    // Rentrée modifiable sur la carte, comme à la création. La sortie, elle,
    // se déplace depuis la bulle de son marqueur.
    secondary.append(
      button(t.t('way_entry_change'), () => {
        close()
        options.onPickFlow?.()
        this.deps.picker.startTrackPick(
          track,
          t.t('way_pick_entry_hint'),
          (candidates) => {
            if (candidates.length === 0) return
            this.deps.chooseKm(track, candidates, (entryKm) => {
              void this.#applyChange(track, { ...way, entryKm }, true)
              onDone()
            })
          },
        )
      }),
    )

    if (way.vias.length < LIMITS.MAX_VIAS_PER_WAY) {
      secondary.append(
        button(t.t('way_via_add'), () => {
          close()
          options.onPickFlow?.()
          this.deps.picker.startMapPick(
            t.t('way_pick_via_hint'),
            (position) => {
              // La liste est réordonnée : le point prend sa place dans le
              // trajet plutôt que d'être ajouté à la fin.
              const vias = this.#orderViasFor(track, way.exitKm, way.entryKm, [
                ...way.vias,
                { lat: position.lat, lon: position.lon },
              ])
              void this.#applyChange(track, { ...way, vias }, true)
              onDone()
            },
          )
        }),
      )
    }

    if (way.vias.length > 0) {
      secondary.append(
        button(t.t('way_via_remove_all'), () => {
          confirmDialog(document.body, {
            title: t.t('way_via_title'),
            message: t.t(
              way.vias.length === 1
                ? 'way_via_remove_all_confirm_one'
                : 'way_via_remove_all_confirm',
              { count: way.vias.length },
            ),
            confirmLabel: t.t('ok'),
            cancelLabel: t.t('cancel'),
            onConfirm: () => {
              close()
              void this.#applyChange(track, { ...way, vias: [] }, true)
              onDone()
            },
          })
        }),
      )
    }

    // Recalcul forcé : même sans changement de saisie, le réseau routier a pu
    // évoluer depuis une géométrie importée. Les points de passage sont
    // réordonnés au passage, ce qui répare un trajet incohérent.
    secondary.append(
      button(t.t('way_recalculate'), () => {
        close()
        const values = read()
        void this.#applyChange(
          track,
          {
            ...way,
            ...values,
            vias: this.#orderViasFor(
              track,
              values.exitKm,
              values.entryKm,
              way.vias,
            ),
          },
          true,
        )
        onDone()
      }),
    )

    body.append(secondary)

    close = showDialog(document.body, {
      title: t.t('edit_way'),
      body,
      dismissible: true,
      actions: [
        {
          label: t.t('marker_delete'),
          destructive: true,
          onClick: () =>
            confirmDialog(document.body, {
              title: t.t('edit_way'),
              message: t.t('way_delete_confirm'),
              confirmLabel: t.t('marker_delete'),
              cancelLabel: t.t('cancel'),
              onConfirm: () => {
                this.delete(track, way.id)
                onDone()
              },
            }),
        },
        { label: t.t('cancel') },
        {
          label: t.t('ok'),
          primary: true,
          onClick: () => {
            const values = read()
            const moved =
              values.exitKm !== way.exitKm || values.entryKm !== way.entryKm
            void this.#applyChange(track, { ...way, ...values }, moved)
            onDone()
          },
        },
      ],
    })
  }

  /* -------------------------------------------------------------------
   * Points de passage sur la carte
   * ---------------------------------------------------------------- */

  /** Tap sur le rond d'un point de passage : le déplacer ou le retirer. */
  showViaActions(track: GpxTrack, wayId: string, viaIndex: number): void {
    const way = track.wayInOuts.find((candidate) => candidate.id === wayId)
    if (!way || viaIndex < 0 || viaIndex >= way.vias.length) return

    const t = this.deps.translator

    showDialog(document.body, {
      title: t.t('way_via_title'),
      dismissible: true,
      actions: [
        {
          label: t.t('way_via_delete'),
          destructive: true,
          onClick: () =>
            confirmDialog(document.body, {
              title: t.t('way_via_title'),
              message: t.t('way_via_delete_confirm'),
              confirmLabel: t.t('marker_delete'),
              cancelLabel: t.t('cancel'),
              onConfirm: () => {
                const vias = way.vias.filter((_, index) => index !== viaIndex)
                void this.#applyChange(track, { ...way, vias }, true)
              },
            }),
        },
        { label: t.t('cancel') },
        {
          label: t.t('way_via_move'),
          primary: true,
          onClick: () => {
            this.deps.picker.startMapPick(
              t.t('way_pick_via_hint'),
              (position) => {
                const vias = way.vias.map((via, index) =>
                  index === viaIndex
                    ? { lat: position.lat, lon: position.lon }
                    : via,
                )
                void this.#applyChange(track, { ...way, vias }, true)
              },
            )
          },
        },
      ],
    })
  }

  /* -------------------------------------------------------------------
   * Utilitaires
   * ---------------------------------------------------------------- */

  /** Reconstruit les marqueurs dérivés puis notifie le reste de l'application. */
  #syncMarkers(track: GpxTrack): void {
    track.rebuildWayOutMarkers(this.deps.wayOutSymbol())
    this.deps.onTrackChanged(track)
  }

  /**
   * Ordonne les points de passage entre les deux ancrages du détour.
   *
   * Sans ancrage résoluble — point kilométrique hors trace — l'ordre fourni est
   * conservé : mieux vaut un trajet dans l'ordre des taps qu'un tri arbitraire.
   */
  #orderViasFor(
    track: GpxTrack,
    exitKm: number,
    entryKm: number,
    vias: readonly WayVia[],
  ): WayVia[] {
    if (vias.length < 2) return [...vias]

    const exit = track.pointAtDistance(track.markerKmToRawDistance(exitKm))
    const entry = track.pointAtDistance(track.markerKmToRawDistance(entryKm))
    if (!exit || !entry) return [...vias]

    return orderVias(exit, entry, vias)
  }
}
